import React from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Mail, Users, CheckCircle, XCircle, Search } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AdminNewsletter() {
  const [inscritos, setInscritos] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showModal, setShowModal] = React.useState(false);
  const [nome, setNome] = React.useState('');
  const [email, setEmail] = React.useState('');

  React.useEffect(() => {
    fetchInscritos();
  }, []);

  const fetchInscritos = async () => {
    try {
      const response = await api.get('/newsletter/inscritos');
      setInscritos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar inscritos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInscrito = async (e) => {
    e.preventDefault();
    if (!nome || !email) {
      toast.error('Preencha nome e e-mail');
      return;
    }

    try {
      await api.post('/newsletter/inscrever', {
        nome,
        email,
        categorias_interesse: []
      });
      toast.success('Inscrito adicionado com sucesso!');
      setShowModal(false);
      setNome('');
      setEmail('');
      fetchInscritos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar inscrito');
    }
  };

  const handleToggleStatus = async (inscritoId) => {
    try {
      await api.put(`/newsletter/inscritos/${inscritoId}/toggle`);
      toast.success('Status alterado');
      fetchInscritos();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleRemoveInscrito = async (inscritoId) => {
    if (!window.confirm('Remover este inscrito?')) return;
    try {
      await api.delete(`/newsletter/inscritos/${inscritoId}`);
      toast.success('Inscrito removido');
      fetchInscritos();
    } catch (error) {
      toast.error('Erro ao remover inscrito');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const filteredInscritos = inscritos.filter(i => 
    i.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = inscritos.filter(i => i.ativo).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-newsletter">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">Newsletter</h1>
            <p className="text-gray-600">{inscritos.length} inscrito(s) - {activeCount} ativo(s)</p>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-[#B91C1C] hover:bg-[#991B1B]" data-testid="add-subscriber-btn">
            <Plus size={18} className="mr-2" />
            Adicionar Inscrito
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-3xl font-bold">{inscritos.length}</p>
                </div>
                <Mail className="text-[#B91C1C]" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ativos</p>
                  <p className="text-3xl font-bold text-green-600">{activeCount}</p>
                </div>
                <CheckCircle className="text-green-600" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Inativos</p>
                  <p className="text-3xl font-bold text-gray-500">{inscritos.length - activeCount}</p>
                </div>
                <XCircle className="text-gray-500" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input placeholder="Buscar por nome ou e-mail..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : filteredInscritos.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Users className="mx-auto mb-4 text-gray-300" size={48} />
              <p>Nenhum inscrito encontrado</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Nome</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">E-mail</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Data</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInscritos.map((inscrito) => (
                      <tr key={inscrito.inscrito_id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{inscrito.nome}</td>
                        <td className="py-3 px-4 text-gray-600">{inscrito.email}</td>
                        <td className="py-3 px-4">
                          {inscrito.ativo ? (
                            <Badge className="bg-green-100 text-green-800">Ativo</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Inativo</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">{formatDate(inscrito.created_at)}</td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleToggleStatus(inscrito.inscrito_id)}>
                              {inscrito.ativo ? 'Desativar' : 'Ativar'}
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleRemoveInscrito(inscrito.inscrito_id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Adicionar Inscrito</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddInscrito} className="space-y-4">
              <div>
                <Label>Nome *</Label>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome completo" className="mt-1" />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.com" className="mt-1" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#B91C1C] hover:bg-[#991B1B]">
                  <Plus size={18} className="mr-2" />
                  Adicionar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
